import Vue from 'vue';
import ct from 'countries-and-timezones';

import store from '@/store/index';
import { authStoreTypes, memberStoreTypes } from '@/store/types';
import { memberRegistrationEndpointsTypes } from '@/http/endpoints/MemberRegistrationEndpoints';

function createMapper() {
  function getDefaultValues() {
    return {
      account: {
        email: '',
        password: '',
        phoneNumber: '+1//',
        marketingSmsOptIn: false,
      },
      saml: {
        enabled: false,
      },
    };
  }

  function parseFromAPI(apiData) {
    if (!apiData) return null;

    const defaultValues = getDefaultValues();
    const formData = getDefaultValues();

    const { data: userData, configs } = apiData;
    formData.saml = configs.saml ?? defaultValues.saml;
    formData.account = {
      ...formData.account,
      email: configs.email ?? defaultValues.account.email,
      phoneNumber: userData.phoneNumber ?? defaultValues.account.phoneNumber,
    };

    return formData;
  }

  function parseToAPI(formData) {
    const { account, auth } = formData;

    const userTimezone = ct.getTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const apiData = {
      password: account.password,
      password_confirmation: account.password,
      timezone: userTimezone?.deprecated ? userTimezone?.aliasOf : userTimezone?.name,
      phone: {
        number: account.phoneNumber.split('//')[1],
        country_code: account.phoneNumber.split('//')[0],
      },
      marketing_sms_opt_in: account.marketingSmsOptIn,
      auth,
    };

    return apiData;
  }

  return { getDefaultValues, parseFromAPI, parseToAPI };
}

function createConstants() {
  const LOADING_TITLE = 'onboarding_message_just_a_moment';
  const LOADING_MESSAGES = {
    SETUP: {
      title: LOADING_TITLE,
      text: 'onboarding2_lovely_to_meet_you',
    },
    SUBMIT: {
      title: LOADING_TITLE,
      text: 'onboarding2_account_creating_account',
    },
    ACCOUNT_CREATED: {
      title: 'eco_loading_woohoo',
      text: 'eco_loading_account_created',
    },
  };

  return { LOADING_MESSAGES };
}

const mapper = createMapper();
export const REGISTER_ACCOUNT_CONSTANTS = createConstants();

class RegisterAccountService {
  constructor() {
    this.serviceLoaded = false;

    const defaultValues = mapper.getDefaultValues();
    this.account = defaultValues.account;
    this.version = undefined;
    this.canSubmit = undefined;
  }

  getServiceData() {
    return {
      account: { ...this.account },
    };
  }

  setServiceData(eligibilityData) {
    this.account = { ...eligibilityData.account };
    this.saml = eligibilityData.saml;
  }

  resetData() {
    this.serviceLoaded = false;
    this.setServiceData(mapper.getDefaultValues());
  }

  async setup(enrolmentUUID) {
    if (!enrolmentUUID) throw new Error('User without enrolment uuid');
    if (this.serviceLoaded) return;

    const { data } = await Vue.$http(memberRegistrationEndpointsTypes.GET_ACCOUNT_REGISTRATION, { enrolmentUUID });
    this.canSubmit = data.can_submit;
    const formData = mapper.parseFromAPI(data);
    this.setServiceData(formData);
    this.version = data.version;

    this.serviceLoaded = true;
  }

  async submit(enrolmentUUID) {
    const formData = this.getServiceData();
    const accountData = mapper.parseToAPI(formData);
    const payload = { data: accountData, version: this.version };
    const { data } = await Vue.$http(memberRegistrationEndpointsTypes.SAVE_ACCOUNT_REGISTRATION, { enrolmentUUID }, { body: payload });

    await store.dispatch(authStoreTypes.actions.SAVE_AUTH_DATA, {
      accessToken: data.auth.access_token,
      refreshToken: data.auth.refresh_token,
    });
    await store.dispatch(memberStoreTypes.actions.GET_MEMBER, null, { root: true });
    store.commit(authStoreTypes.mutations.SET_AUTH_HAS_TIMEDOUT, false);

    this.resetData();
  }
}

export default new RegisterAccountService();
